import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { RadioFormConfig } from '@ajgre/toolkit';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 're-radio',
  imports: [],
  templateUrl: './radio.component.html',
  styleUrls: ['./radio.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioComponent),
      multi: true
    }
  ]
})
export class RadioComponent implements ControlValueAccessor, OnChanges {
  private renderer = inject(Renderer2);

  @Input() formControlName?: string;
  @Input() formConfig!: RadioFormConfig;
  @Input() value?: string;
  @Output() valueChange = new EventEmitter<string>();
  @Input() disabled = false;
  @Input() errorMessage = '';
  @ViewChild('radio', { static: true }) radio = {} as ElementRef<HTMLInputElement>;

  propagateChange: (_: unknown) => unknown = () => null;
  propagateTouch: (_: unknown) => unknown = () => null;

  @HostBinding('class.ng-invalid') get invalid() {
    return !this.formControlName && this.errorMessage && !this.disabled;
  }
  @HostBinding('class.ng-touched') get touched() {
    return !this.formControlName && this.errorMessage && !this.disabled;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.formControlName) {
      if (changes['value']) {
        this.writeValue(changes['value'].currentValue);
      }
      if (changes['disabled']) {
        this.setDisabledState(changes['disabled'].currentValue);
      }
    }
  }

  displayErrorMessage(): boolean {
    return !!this.errorMessage && (this.formConfig.showErrorMessage ?? true) && !this.disabled;
  }

  writeValue(value: string) {
    if (this.formConfig.selectedValue === value) {
      this.renderer.setAttribute(this.radio.nativeElement, 'checked', '');
      this.radio.nativeElement.checked = true;
    } else {
      this.renderer.removeAttribute(this.radio.nativeElement, 'checked');
      this.radio.nativeElement.checked = false;
    }
  }

  onChange(value: string) {
    this.propagateChange(value);

    this.valueChange.emit(value);
  }

  onBlur(event: FocusEvent) {
    this.propagateTouch(event);
  }

  registerOnChange(fn: (_: unknown) => unknown) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: (_: unknown) => unknown) {
    this.propagateTouch = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.renderer.setAttribute(this.radio.nativeElement, 'disabled', '');
    } else {
      this.renderer.removeAttribute(this.radio.nativeElement, 'disabled');
    }
  }
}
